/* eslint-disable no-nested-ternary */
import React, { forwardRef } from 'react';
import MenuExpandIcon from '@atlaskit/icon/core/migration/menu--menu-expand';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useGetGroupByField } from '../../../../../controllers/get-group-by-field/index.tsx';
import { useGroupBy } from '../../../../../controllers/group-by/index.tsx';
import messages from './messages.tsx';

const GROUP_ICON = <MenuExpandIcon label="" LEGACY_size="small" />;

type Props = Partial<Omit<TriggerProps, 'ref'>> & { isSelected: boolean; onClick: () => void };

const Trigger = forwardRef<HTMLDivElement, Props>(
	({ onClick, isSelected, ...otherProps }: Props, ref) => {
		const { formatMessage } = useIntl();
		const [groupByFieldId] = useGroupBy();

		const groupByField = useGetGroupByField();

		const groupByButtonText = formatMessage(messages.groupByButton);
		const selectedGroupButtonText = (
			<FormattedMessage
				{...messages.groupByActiveButton}
				values={{
					groupByFieldId: groupByField?.name,
				}}
			/>
		);

		if (isVisualRefreshEnabled()) {
			const visuallyRefreshedContent = fg('visual-refresh-headers-buttons') ? (
				<>
					{groupByFieldId == null ? (
						isSelected ? (
							groupByButtonText
						) : (
							<Box xcss={triggerStyles}>
								<Text color="color.text.subtle" weight="medium" maxLines={1}>
									{groupByButtonText}
								</Text>
							</Box>
						)
					) : (
						selectedGroupButtonText
					)}
				</>
			) : (
				<>{groupByFieldId == null ? groupByButtonText : selectedGroupButtonText}</>
			);

			return (
				<ThemedButton
					ref={ref}
					aria-label={formatMessage(messages.groupByActiveButtonAriaLabel, {
						label: groupByField?.name,
					})}
					testId="business-list.ui.list-view.header-actions.group-by-dropdown.trigger.button-wrapper"
					isSelected={isSelected || groupByFieldId != null}
					onClick={onClick}
					iconAfter={
						groupByFieldId == null ? (
							<ChevronDownIcon
								label=""
								{...(!isSelected &&
									fg('visual-refresh-headers-buttons') && {
										color: token('color.icon.subtle'),
									})}
								{...(!fg('visual-refresh-headers-buttons') && {
									color: 'currentColor',
								})}
							/>
						) : undefined
					}
					{...otherProps}
				>
					<Flex as="span" columnGap="space.075" alignItems="center">
						{visuallyRefreshedContent}
					</Flex>
				</ThemedButton>
			);
		}

		return (
			<ThemedButton
				ref={ref}
				testId="business-list.ui.list-view.header-actions.group-by-dropdown.trigger.button-wrapper"
				iconBefore={GROUP_ICON}
				isSelected={isSelected}
				onClick={onClick}
				appearance="subtle"
				{...otherProps}
			>
				{groupByFieldId == null ? (
					groupByButtonText
				) : (
					<FormattedMessage
						{...messages.groupByActiveButtonOld}
						values={{
							groupByFieldId: groupByField?.name,
						}}
					/>
				)}
			</ThemedButton>
		);
	},
);

export default Trigger;

const triggerStyles = xcss({
	paddingTop: 'space.075',
	paddingBottom: 'space.075',
	pointerEvents: 'none',
});
